/**
 * GiftGridItem
 * Single gift cell in the SendGiftSheet grid.
 *
 * Flutter: SendGiftBottomSheet.gridWidget item
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import type { GiftEntity } from '@/domain/entities/gift/giftEntity';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { GiftUtils } from '@/domain/utils/giftUtils';
import { MathUtils } from '@/domain/utils/mathUtils';

interface GiftGridItemProps {
  gift: GiftEntity;
  selected: boolean;
  instances: number;
  onTap: () => void;
}

export const GiftGridItem: React.FC<GiftGridItemProps> = ({ gift, selected, instances, onTap }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  /** Display-only ceil — rationale and Flutter citations live on the helper. */
  const displayCoins = MathUtils.ceilCoinsForDisplay(gift.coins);

  return (
    <button
      type="button"
      {...qaAttr({ id: `gift-item-${gift.id}`, ...(selected ? { value: 'selected' } : {}) })}
      onClick={onTap}
      className="relative flex flex-col items-center justify-center transition-transform active:scale-95"
      style={{
        padding: responsive.spacing.sm,
        borderRadius: responsive.borderRadius.lg,
        border: `${responsive.border.thin}px solid ${selected ? theme.exclusive : 'transparent'}`,
        background: 'none',
        cursor: 'pointer',
        gap: responsive.spacing.xs,
        /**
         * The cell this button lives in is a FIXED-height box: `VirtualList`
         * grid mode derives the row height from container width / column count /
         * `itemAspectRatio` and clips every cell with `overflow: hidden`. So the
         * tile has to be able to fit an arbitrarily short cell instead of
         * overflowing and being trimmed at the bottom.
         *
         * `height: 100%` binds the button to the cell box and `minHeight: 0`
         * waives the grid item's automatic minimum so the auto-sized cell row can
         * be exactly the cell height (without it the row is floored at the tile's
         * intrinsic min-content height and overflows). The tile itself sets NO
         * `overflow`: the `VirtualList` grid cell already clips (its cell style),
         * and since `height: 100%` makes the button's border box coincident with
         * that cell box, a tile-level clip could hide nothing the cell does not
         * already hide — it would only round the clip against `borderRadius.lg`
         * and shave the absolutely-positioned instances badge in the top-right
         * corner.
         */
        height: '100%',
        minHeight: 0,
      }}
    >
      {/*
        Square, shrinkable image box. `flex-basis` stays the natural
        `avatar.medium`, so WITH SLACK (the shipped 3-column grid) nothing
        shrinks and the tile renders exactly as before this box existed. When
        the cell is short the box shrinks and `CONTENT_CONSTANTS.squareAspectRatio`
        keeps it square, so `cover` never crops the (square) gift icon — a
        height-only shrink with a fixed width would centre-crop the icon and
        merely move the defect.

        The wrapper is required rather than styling `CachedImage` directly
        because `CachedImage` spreads the same `style` onto its loading
        placeholder and error `div`s — an `auto`-width placeholder would collapse
        to zero width and the grey loading square would vanish.
      */}
      <div
        style={{
          height: responsive.avatar.medium,
          aspectRatio: CONTENT_CONSTANTS.squareAspectRatio,
          maxHeight: '100%',
          minHeight: 0,
          flexShrink: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CachedImage
          src={GiftUtils.getGiftPath(gift.image)}
          alt={gift.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
      {/*
        Coins row: `flexShrink: 0` so every pixel of a short-cell shortfall is
        absorbed by the image box above and the price + coins icon are never
        trimmed. Below roughly `padding + border + coins row` the image box
        floors at zero and the coins row is the last thing standing — that
        ordering is deliberate.

        Web DELIBERATELY INVERTS Flutter's shrink priority here. Flutter pins the
        40 x 40 gift icon (`DimensionConstants.giftIconSize`,
        `send_gift_bottom_sheet.dart:589-593`) and lets the `Flexible`-wrapped
        coins row yield (`:596`); web pins the price row and shrinks the icon
        box, because on web the price is the informative element and the icon is
        decorative. Same
        graceful-degradation intent, and which child yields is short-cell layout
        density — web-UI territory (`presentation.md` "Web vs Flutter UI parity
        scope"), not a parity divergence.
      */}
      <div className="flex items-center" style={{ gap: responsive.spacing.xs, flexShrink: 0 }}>
        <span
          {...qaAttr({ id: `gift-item-${gift.id}-cost`, value: displayCoins })}
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.medium,
          }}
        >
          {displayCoins}
        </span>
        <AssetIcon
          path="/assets/images/coins.png"
          width={responsive.icon.xs}
          height={responsive.icon.xs}
          color={theme.exclusive}
        />
      </div>

      {selected && instances > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: theme.danger,
            borderRadius: responsive.borderRadius.base,
            paddingLeft: responsive.spacing.sm,
            paddingRight: responsive.spacing.sm,
            color: theme.text.primary,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.semibold,
            minWidth: responsive.icon.small,
            textAlign: 'center',
          }}
        >
          {instances}
        </div>
      )}
    </button>
  );
};
